/**
 * <g-column> - Kanban Column
 *
 * A single column in a kanban board with header and droppable card area.
 *
 * @example
 * <g-column status="todo" label="To Do" sublabel="3 tasks" color="purple">
 *   <g-card card-id="1">...</g-card>
 * </g-column>
 *
 * @attr {string} status - Status value for cards in this column
 * @attr {string} label - Display label in header
 * @attr {string} color - Top border color: purple, blue, orange, green, cyan, or hex value
 * @attr {string} sublabel - Optional sublabel (e.g., "3 deals · $450K")
 */
export class GColumn extends HTMLElement {
  static get observedAttributes() {
    return ['label', 'color', 'sublabel'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.isConnected) {
      this.updateHeader();
    }
  }

  getColor(color) {
    const colors = {
      purple: '#8b5cf6',
      blue: '#3b82f6',
      cyan: '#06b6d4',
      orange: '#f97316',
      green: '#22c55e',
      red: '#ef4444',
      pink: '#ec4899',
      yellow: '#eab308',
      gray: '#6b7280'
    };
    return colors[color] || color || colors.gray;
  }

  render() {
    const colorAttr = this.getAttribute('color');
    const color = colorAttr ? this.getColor(colorAttr) : null;

    // Use Tailwind classes for background
    this.classList.add('bg-base-200');

    // Apply other styles inline
    this.style.display = 'flex';
    this.style.flexDirection = 'column';
    this.style.minWidth = '280px';
    this.style.maxWidth = '320px';
    this.style.flexShrink = '0';
    this.style.borderRadius = '0.75rem';
    this.style.padding = '0.75rem';
    this.style.paddingTop = '0';

    // Optional colored top border
    if (color) {
      this.style.borderTop = `4px solid ${color}`;
    }

    // Create header
    this.updateHeader();
  }

  updateHeader() {
    const label = this.getAttribute('label') || '';
    const sublabel = this.getAttribute('sublabel') || '';

    let header = this.querySelector('.g-column-header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'g-column-header';
      this.insertBefore(header, this.firstChild);
    }

    header.style.cssText = `
      padding: 0.75rem 0.25rem;
      margin-bottom: 0.25rem;
    `;

    header.innerHTML = `
      <div class="font-semibold text-base-content">${label}</div>
      ${sublabel ? `<div class="text-xs text-base-content/50 mt-0.5">${sublabel}</div>` : ''}
    `;
  }
}

customElements.define('g-column', GColumn);
